import { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { ChevronsUpDown, Loader2, X } from 'lucide-react';
import type { ContributorToken } from '@sleed/bizcommerce-api';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import { Command, CommandEmpty, CommandGroup, CommandInput, CommandItem, CommandList } from '@/components/ui/command';
import { contributorsApi } from '~/lib/api/client';

const DEBOUNCE_MS = 300;
const PAGE_SIZE = 20;

export interface ContributorComboboxValue {
	id: number;
	name: string;
}

interface Props {
	value: ContributorComboboxValue | null;
	onChange: (value: ContributorComboboxValue) => void;
	onClear?: () => void;
	disabled?: boolean;
}

const fullName = (c: ContributorToken) => `${c.fn ?? ''} ${c.ln ?? ''}`.trim();

/**
 * Single-contributor async search by name/surname (ContributorsController getAll `searchText`, the legacy
 * ContributorSelect source) — the author of an editorial review.
 */
export function ContributorCombobox({ value, onChange, onClear, disabled }: Props) {
	const { t } = useTranslation();
	const [open, setOpen] = useState(false);
	const [query, setQuery] = useState('');
	const [results, setResults] = useState<ContributorToken[]>([]);
	const [loading, setLoading] = useState(false);
	// Latest-query guard so a slow earlier response can't overwrite a newer one.
	const requestSeq = useRef(0);

	useEffect(() => {
		if (!open) return;
		const term = query.trim();
		if (!term) {
			setResults([]);
			setLoading(false);
			return;
		}
		let cancelled = false;
		const seq = ++requestSeq.current;
		setLoading(true);
		const handle = setTimeout(() => {
			contributorsApi
				.getAll({
					loadOptions: { skip: 0, take: PAGE_SIZE, sort: [{ selector: 'ln', desc: false }] },
					// `categories` is a required (non-nullable) field server-side — always send an array.
					additionalFiltering: { searchText: term, categories: [] }
				})
				.then(res => {
					if (!cancelled && seq === requestSeq.current) setResults(res.data.data ?? []);
				})
				.catch(() => {
					if (!cancelled && seq === requestSeq.current) setResults([]);
				})
				.finally(() => {
					if (!cancelled && seq === requestSeq.current) setLoading(false);
				});
		}, DEBOUNCE_MS);
		return () => {
			cancelled = true;
			clearTimeout(handle);
		};
	}, [query, open]);

	return (
		<div className="flex min-w-0 items-center gap-1">
			<Popover open={open} onOpenChange={setOpen}>
				<PopoverTrigger asChild disabled={disabled}>
					<button
						type="button"
						className="flex h-9 min-w-0 flex-1 items-center justify-between gap-2 rounded-md border border-border bg-surface px-2 text-left text-sm text-foreground disabled:cursor-not-allowed disabled:opacity-50">
						{value ? (
							<span className="truncate">{value.name || `#${value.id}`}</span>
						) : (
							<span className="truncate text-foreground-subtle">{t('catalog.reviews.fields.pickContributor')}</span>
						)}
						<ChevronsUpDown size={14} className="shrink-0 text-foreground-subtle" />
					</button>
				</PopoverTrigger>
				<PopoverContent className="w-[360px] p-0" align="start">
					<Command shouldFilter={false}>
						<CommandInput value={query} onValueChange={setQuery} placeholder={t('catalog.reviews.fields.searchContributor')} />
						<CommandList>
							{loading ? (
								<div className="flex items-center justify-center gap-2 py-6 text-sm text-foreground-muted">
									<Loader2 size={14} className="animate-spin" /> {t('common.loading')}
								</div>
							) : (
								<>
									<CommandEmpty>{query.trim() ? t('common.noResults') : t('catalog.reviews.fields.searchContributorHint')}</CommandEmpty>
									<CommandGroup>
										{results.map(contributor => (
											<CommandItem
												key={contributor.id}
												value={String(contributor.id)}
												onSelect={() => {
													if (contributor.id == null) return;
													onChange({ id: contributor.id, name: fullName(contributor) });
													setOpen(false);
												}}>
												<span className="min-w-0 flex-1 truncate">{fullName(contributor) || `#${contributor.id}`}</span>
											</CommandItem>
										))}
									</CommandGroup>
								</>
							)}
						</CommandList>
					</Command>
				</PopoverContent>
			</Popover>
			{onClear && value && !disabled && (
				<button
					type="button"
					onClick={onClear}
					aria-label={t('common.remove')}
					className="rounded p-1 text-foreground-subtle hover:bg-elevated hover:text-foreground">
					<X size={14} />
				</button>
			)}
		</div>
	);
}
